'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { getCookie } from 'cookies-next';
import { useRouter } from 'next/navigation';
import { updateSharedCookieFields } from '@/utils/cookieUtils';

// Define the shape of a User object
interface User {
  email: string;
  firstName: string;
  lastName: string;
  authExpiration: Date | null;
}

// Define the shape of the authentication context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  logout: () => void;
}

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component to wrap your app and manage auth state
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null); // User state
  const router = useRouter(); // For client-side navigation

  // Parse and return user data from cookie, or null if invalid/missing
  const parseUserFromCookie = (): User | null => {
    const raw = getCookie('MySchumacherIsShared');
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw as string);
      const exp = parsed.AuthExpirationDateTime
        ? new Date(parsed.AuthExpirationDateTime)
        : null;

      // Ensure essential fields are present and valid
      if (!parsed.UserEmail || !exp || isNaN(exp.getTime())) {
        return null;
      }

      return {
        email: parsed.UserEmail ?? '',
        firstName: parsed.FirstName ?? '',
        lastName: parsed.LastName ?? '',
        authExpiration: exp,
      };
    } catch (e) {
      console.error('Failed to parse auth cookie:', e);
      return null;
    }
  };

  // Logout function to clear session and redirect to home
  const logout = async () => {
    try {
      const response = await fetch(
        process.env.NEXT_PUBLIC_LOGOUT_URL ||
          'https://betawww.schumacherhomes.com/umbraco/api/AccountApi/HandleLogout',
        {
          method: 'POST',
          credentials: 'include', // Include cookies in request
        },
      );

      if (!response.ok) {
        // Handle unsuccessful logout attempt
        console.error('Logout failed with status:', response.status);
        return;
      }

      // Clear cookie fields and user state on successful logout
      updateSharedCookieFields({
        UserEmail: null,
        FirstName: null,
        LastName: null,
        AuthExpirationDateTime: null,
      });

      setUser(null); // Clear user state
      router.push('/'); // Redirect to home
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // useEffect to initialize auth state and handle session expiration
  useEffect(() => {
    const raw = getCookie('MySchumacherIsShared');
    let current: Record<string, any> = {};

    try {
      current = raw ? JSON.parse(raw as string) : {};
    } catch {
      current = {};
    }

    // Ensure all expected cookie fields are initialized
    const needsInit =
      current.UserEmail === undefined ||
      current.FirstName === undefined ||
      current.LastName === undefined ||
      current.AuthExpirationDateTime === undefined;

    if (needsInit) {
      updateSharedCookieFields({
        UserEmail: current.UserEmail ?? null,
        FirstName: current.FirstName ?? null,
        LastName: current.LastName ?? null,
        AuthExpirationDateTime: current.AuthExpirationDateTime ?? null,
      });
    }

    // Load user info from cookie into state
    const userFromCookie = parseUserFromCookie();
    setUser(userFromCookie);

    // Function to check if session has expired
    const checkExpiration = () => {
      if (userFromCookie?.authExpiration) {
        const now = new Date();
        if (now >= userFromCookie.authExpiration) {
          console.warn('Session expired');
          logout(); // Log user out if expired
        }
      }
    };

    // Check immediately and then every minute
    checkExpiration();
    const interval = setInterval(checkExpiration, 60 * 1000);

    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, []);

  // Provide authentication context to children
  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to consume the AuthContext safely
export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
